/** Navigation groups, never sequential gates. Facts remain in their domain records. */
export const onboardingGroups = [
    ['authority', 'Contract, Client and Authority'],
    ['profile', 'Building Profile'],
    ['commercial', 'Fees, Services and Spending Authority'],
    ['properties', 'Properties, Leases and Occupancy'],
    ['charging', 'Service-Charge Structure and Billing Rules'],
    ['finance', 'Finance Setup and Handover Balances'],
    ['assets', 'Assets, Facilities and Utilities'],
    ['suppliers', 'Suppliers, Contracts and People'],
    ['condition', 'Condition, Repairs and Projects'],
    ['safety', 'Compliance, Fire and Building Safety'],
    ['insurance', 'Insurance and Claims'],
    ['launch', 'Access, Communications and Resident Launch'],
    ['acceptance', 'Go-Live Review and Acceptance'],
];
export const onboardingFollowUpStates = ['Unknown', 'Missing', 'Awaiting information', 'In progress', 'Awaiting review'];
export const goLiveGroups = ['authority', 'profile', 'properties', 'insurance', 'safety', 'acceptance'];
export const goLiveTaskIds = ['bank', 'emergency', 'works'];
/** Groups that hold one of the go-live tasks, so they are listed before go-live too. */
const goLiveTaskGroups = ['finance', 'launch', 'condition'];
export function onboardingTaskPhase(task) {
    // The inventory only reports out-of-service life-safety or high-criticality assets as critical issues.
    const criticalAsset = task.id === 'assets' && Boolean(task.criticalIssues?.length);
    return criticalAsset || goLiveGroups.includes(task.group) || goLiveTaskIds.includes(task.id) ? 'Before go-live' : 'After go-live';
}
/** Days after go-live used as the default due date for after-go-live tasks without their own due date. */
export const afterGoLiveDueDays = 90;
export const onboardingStaffDecisions = ['Done', 'Not applicable', 'Reopen'];
/** Onboarding tasks that have no readiness projection of their own. */
export const staffConfirmedOnboardingTaskIds = ['workforce', 'claims', 'launch'];
/** The same saved-record predicate drives legacy groups, the guide and the rail. */
export function taskRecordComplete(task) {
    return task.accessible && task.applicability === 'Applicable' && task.state === 'Complete'
        && !task.criticalIssues.length && (!task.awaitingReview || task.reviewBlocksCompletion === false);
}
/** Display the effective checklist position, not just the owning record's state. */
export function onboardingTaskDisplayState(task) {
    if (!task.accessible)
        return 'Access required';
    if (task.applicability === 'Not applicable')
        return 'Not applicable';
    if (task.criticalIssues.length)
        return 'Needs attention';
    if (task.state === 'Complete' && !taskRecordComplete(task)) {
        return task.applicability === 'Unknown' ? 'Applicability not confirmed' : 'Awaiting review';
    }
    return task.state;
}
/** One mission over the canonical checklist, never a second task/completion store. */
export function onboardingMission(model) {
    const tasks = model.groups.flatMap(group => group.tasks);
    const applicable = tasks.filter(task => task.applicability !== 'Not applicable');
    const complete = taskRecordComplete;
    const outstanding = applicable.filter(task => !complete(task));
    const priority = (task) => !task.accessible ? 4 : task.criticalIssues.length
        ? (task.phase ?? onboardingTaskPhase(task)) === 'Before go-live' ? 0 : 1
        : task.awaitingReview || task.state === 'Awaiting review' ? 2 : 3;
    const nextTask = [...outstanding].sort((a, b) => priority(a) - priority(b))[0] ?? null;
    const dueDate = (task) => task.dueDate
        ?? ((task.phase ?? onboardingTaskPhase(task)) === 'Before go-live' ? model.goLiveDate : model.afterGoLiveDueDate) ?? null;
    const owner = (task) => task.ownerId
        ? model.owners.find(item => item.id === task.ownerId) ?? { id: task.ownerId, name: 'Assigned staff member' }
        : model.buildingLead ?? null;
    return {
        owner: model.buildingLead ?? null,
        state: !tasks.length ? 'Unknown' : !outstanding.length ? 'Complete'
            : outstanding.some(task => task.accessible && task.criticalIssues.length) ? 'Needs attention' : 'In progress',
        completed: applicable.filter(complete).length, total: applicable.length,
        unavailable: applicable.filter(task => !task.accessible).length,
        awaited: applicable.filter(task => task.accessible && ['Missing', 'Awaiting information'].includes(task.state))
            .map(task => ({ task, owner: owner(task), dueDate: dueDate(task) })),
        next: nextTask ? { task: nextTask, owner: owner(nextTask), dueDate: dueDate(nextTask) } : null,
        completionCondition: 'Every applicable task is accessible, complete and has no unresolved critical issue or pending review.',
    };
}
/** Unknown stays in the denominator. Only an authoritative N/A decision excludes it.
 * Critical risk takes precedence over both completion and review indicators. */
export function summariseOnboarding(tasks) {
    if (new Set(tasks.map(task => task.id)).size !== tasks.length)
        throw new Error('Duplicate onboarding task');
    return onboardingGroups.map(([id, label], index) => {
        const groupTasks = tasks.filter(task => task.group === id);
        const applicable = groupTasks.filter(task => task.applicability !== 'Not applicable');
        const completed = applicable.filter(taskRecordComplete).length;
        const criticalIssues = groupTasks.flatMap(task => task.criticalIssues);
        const awaitingReview = groupTasks.reduce((sum, task) => sum + task.awaitingReview, 0);
        const started = groupTasks.some(task => task.state !== 'Unknown' || task.note || task.ownerId || task.dueDate || task.awaitingReview);
        const complete = groupTasks.length > 0 && completed === applicable.length;
        const phase = goLiveGroups.includes(id) || goLiveTaskGroups.includes(id) || groupTasks.some(task => onboardingTaskPhase(task) === 'Before go-live') ? 'Before go-live' : 'After go-live';
        // Change #40: an issue in after-go-live set-up needs attention but does not block taking the building on.
        const blocking = groupTasks.some(task => onboardingTaskPhase(task) === 'Before go-live' && task.criticalIssues.length > 0);
        const tone = blocking ? 'blocked' : criticalIssues.length ? 'attention' : complete ? 'complete' : started ? 'attention' : 'neutral';
        return { id, number: index + 1, label, completed, total: applicable.length,
            status: blocking ? 'Critical blocker' : criticalIssues.length ? 'Needs attention' : tone === 'complete' ? 'Complete' : tone === 'attention' ? 'Incomplete' : 'Not started',
            tone, criticalIssues, awaitingReview, tasks: groupTasks, phase };
    });
}
//# sourceMappingURL=building-onboarding.js.map